import { Component, HostListener, OnInit, inject } from '@angular/core';
import {
  Observable,
  Subscription,
  UnaryFunction,
  of,
  pipe,
  throwError,
} from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { ApolloError } from '@apollo/client/errors';
import { QueryStringTypesEnum } from 'src/app/configs/query-string-types';
import {
  ComparisonOperator,
  CustomResultOfCheckIsResendByAutomaticResendDto,
  CustomResultOfCheckOutboxMessagesIsResendByAutomaticResendDto,
  ExcelTypes,
  GetOutboxMessagesWithPaginationDto,
  LogicalComparisonOperator,
  SmsCp,
} from 'src/app/core/api/models';
import { EventTypes } from 'src/app/core/constants/event-types';
import { OutBoxMessagesGQLService } from 'src/app/core/graphql/outbox-messages-gql.service';
import { ConsoleService } from 'src/app/core/services/console.service';
import { DefaultSearchValueService } from 'src/app/core/services/default-search-value.service';
import { ScheduledMessageIdsStorageService } from 'src/app/core/services/scheduled-message-ids-storage.service';
import { TabsDynamicComponent } from 'src/app/layout/components/tabs-dynamic/tabs-dynamic.component';
import { VoiceSmsDetailModalComponent } from 'src/app/shared/components/shortcut-sms-detail/voice-sms-detail-modal/voice-sms-detail-modal.component';
import { TableColumnStateService } from 'src/app/shared/global/dynamic-table/table-column-state.service';
import { PageNames } from '../../../../configs/page-names.enum';
import {
  ExcelService,
  OutBoxMessageResendHistoriesService,
  OutboxMessageNumbersService,
  SmartSmsRecipientsService,
  VoiceMessagesService,
} from '../../../../core/api/services';
import { DateTimeService } from '../../../../core/services/date-time.service';
import { DeliveryStatusStats } from '../../shared/delivery-status-stats/delivery-status-stats.component';
import { EditSendReceiversDeliveryStatus } from '../../shared/edit-send-receivers-delivery-status/edit-send-receivers-delivery-status.component';
import { OutboxMessagesEditStatusComponent } from '../../shared/outbox-messages-edit-status/outbox-messages-edit-status.component';
import { IsSmartTypeService } from '../../shared/send-receivers/is-smart-type.service';
import {
  GetUserWithPaginationQuery,
  SendTypes,
} from './../../../../core/api/models';
import { GetEnumValuesDto } from './../../../../core/api/models/get-enum-values-dto';
import { GetOutBoxByScheduledIdDto } from './../../../../core/api/models/get-out-box-by-scheduled-id-dto';
import { OutBoxStatus } from './../../../../core/api/models/out-box-status';
import { ResendByDeliveryStatusCommand } from './../../../../core/api/models/resend-by-delivery-status-command';
import { OutBoxMessagesService } from './../../../../core/api/services/out-box-messages.service';
import { AppDomService } from './../../../../core/services/dom.service';
import { DynamicTabService } from './../../../../core/services/dynamic-tab.service';
import { BaseIndexPageComponent } from './../../../../pages/shared/base/components/base-index-page.component';
import { OutboxMessagesColumnNames } from './columns';
import { CreateOutboxMessagesExcelDialogComponent } from './create-outbox-messages-excel/create-outbox-messages-excel-dialog.component';
import { OutboxAdvancedSearchFormComponent } from './outbox-advanced-search/outbox-advanced-search-form.component';

@Component({
  selector: 'app-base-outbox-messages',
  templateUrl: './base-outbox-messages.component.html',
  styleUrls: ['../../base/base-report.component.scss'],
  standalone: false,
})
export class BaseOutboxMessagesComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region properties

  outBoxMessagesGQLService = inject(OutBoxMessagesGQLService);

  pageName = 'outboxMessages';

  isShowOrganizationSearch: boolean = false;
  tabName: string;
  showReseller: boolean = true;
  modelWithPagination$: Observable<any>;

  getTotalCountApiMethodName: string;
  pageTitle: string;
  excelType: ExcelTypes;

  isShowDetail: boolean = false;
  isShowSendBulkDetail: boolean = false;
  isShowSendSmartDetail: boolean = false;
  isShowPhonebookRecipientInDetail: boolean = false;
  isShowSmartSmsRecipientInDetail: boolean = false;
  isShowPhonebookRecipient: boolean = false;
  isShowResendByDeliveryStatus: boolean = false;
  isShowResendDetail: boolean = false;

  scheduledMessageId: number;
  resellerId: number;
  selectedUserId: number;
  selectedOrganizationId: number;
  selectedUserName: string;
  selectedOrganizationName: string;
  selectedScheduledMessageSendType: SendTypes;
  cpType: SmsCp;
  voiceMessageId: number;

  detailModel: GetOutBoxByScheduledIdDto = {};
  resendByDeliveryStatusModel: ResendByDeliveryStatusCommand = {};
  DeliveryStatusEnumValues: GetEnumValuesDto[] = [];

  resendDetailWithPagination$: Observable<any>;
  totalReceivers: number;
  totalPrice: number;
  count: number;
  voiceMessageGroupId: number;
  calcTotalApiLoading: boolean;

  get SendTypes() {
    return SendTypes;
  }
  get OutBoxStatus() {
    return OutBoxStatus;
  }
  get PageNames() {
    return PageNames;
  }

  OutBoxStatusEnumValues: GetEnumValuesDto[] = [];
  SendTypesEnumValues: GetEnumValuesDto[] = [];
  SendSubTypesEnumValues: GetEnumValuesDto[] = [];
  CreationLocationEnumValues: GetEnumValuesDto[] = [];

  totalPriceCurrentPage: number = 0;
  totalReceiversCurrentPage: number = 0;

  body: any = {};
  calTotalApiBody: any = {};
  organizationId: number;

  subscription: Subscription;
  isAdvancedSearch: boolean = false;
  innerWidth: number;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.innerWidth = window.innerWidth;
  }

  columnNames = OutboxMessagesColumnNames;
  selectedFields: string[] = [];
  fixedFields: string[] = [
    this.columnNames.scheduledMessageId,
    this.columnNames.smsText,
    this.columnNames.sentDateTime,
    this.columnNames.price,
    this.columnNames.receiverCount,
  ];
  idPropertyName = this.columnNames.scheduledMessageId;

  graphQlPipeOperators = <T>() =>
    pipe(
      catchError((error: ApolloError) => {
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          const errors = error?.graphQLErrors;
          const messages = errors?.map((err) => {
            return errors?.length == 1 ? err.message : '• ' + err.message;
          });
          const msgFormatted = messages.join('<br>');
          this.openErrorMsgDialog({ message: msgFormatted });
        } else this.openErrorMsgDialog({ message: error as any });
        this.cd.detectChanges();
        this.loading = false;
        return throwError(() => error);
      })
    ) as UnaryFunction<Observable<T>, Observable<T>>;

  //#endregion

  constructor(
    protected currentService: OutBoxMessagesService,
    public outboxMessageNumbersService: OutboxMessageNumbersService,
    public outboxMessageResendHistoriesService: OutBoxMessageResendHistoriesService,
    public smartSmsRecipientsService: SmartSmsRecipientsService,
    private dynamicTabService: DynamicTabService,
    private scheduledMessageIdsStorageService: ScheduledMessageIdsStorageService,
    private defaultSearchValueService: DefaultSearchValueService,

    public excelFileService: ExcelService,
    protected voiceMessageService: VoiceMessagesService,
    protected tabsDynamicComponent: TabsDynamicComponent
  ) {
    super();
  }

  ngOnInit() {
    try {
      if (this.isShowOrganizationSearch) {
        window.scroll(0, 0);
        this.getUserState();
        this.getEnumsValues();
        this.getVocabs();
        this.configTableColumns();
        this.initTablePagination();
        this.setDefaultTableSort();
        this.configPage();
        this.configFormPage();
      } else super.ngOnInit(false, false);

      this.pageConfig.tableConfig.pageTagName = this.tagName;
      this.pageId = this.tabName;
      this.pageConfig.title = this.pageTitle;
      this.pageConfig.tableConfig.isSticky = true;

      if (window.location.search == '') this.setDefaultDateSearch();
      this.pageConfig.isAddBtnWithLoading = false;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  ngAfterViewChecked(): void {
    this.innerWidth = window.innerWidth;
  }

  // وارد میشود چک میکنیم که کدام جدول باید نمایش داده شود url زمانی که کاربر با
  setPageAfterEnterUrl() {
    super.setPageAfterEnterUrl();
    let params = this.routingState.parseQueryString();
    for (const [key, value] of Object.entries(params)) {
      switch (key) {
        case QueryStringTypesEnum.Details_key:
          this.onDetail(
            +value.toString().split('_')[0],
            +value.toString().split('_')[1],
            false
          );
          break;
        case QueryStringTypesEnum.ResendDetails_key:
          this.onShowResendDetail(+value, false);
          break;
        case QueryStringTypesEnum.ResendByDeliveryStatus_key:
          this.onShowResendByDeliveryStatus(
            {
              scheduledMessageId: +value.toString().split('_')[0],
              userName: value.toString().split('_')[1],
              organizationId: +value.toString().split('_')[2],
              organizationName: value.toString().split('_')[3],
              createdBy: +value.toString().split('_')[4],
              sendType: +value.toString().split('_')[5],
              smsText: value.toString().split('_')[6],
            },
            false
          );
          break;
      }
    }
  }

  //#region init

  getEnumsValues() {
    this.getEnumValuesApi('OutBoxStatus')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.OutBoxStatusEnumValues = values));
    this.getEnumValuesApi('SendTypes')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.SendTypesEnumValues = values));
    this.getEnumValuesApi('SendSubTypes')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.SendSubTypesEnumValues = values));
    this.getEnumValuesApi('DeliveryStatus')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.DeliveryStatusEnumValues = values));
    this.getEnumValuesApi('EntityCreationLocation')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.CreationLocationEnumValues = values));
  }

  configTableColumns() {
    this.tableVocabs = this.pageVocabs + 'fields.';

    this.tableColumns = [
      {
        title: ``,
        name: '',
        hasSearch: false,
        noSorting: true,
        isCheckBox: true,
        wrap: true,
        isSticky: true,
      },
      {
        title: `${this.tableVocabs}scheduledMessageId`,
        name: this.columnNames.scheduledMessageId,
        hasSearch: true,
        type: 'number',
        wrap: true,
        noNumberSeparator: true,
        isSticky: true,
      },
      {
        title: this.localizer.smsText,
        name: this.columnNames.smsText,
        hasSearch: true,
        wrap: true,
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
        isSticky: true,
      },
      {
        title: this.localizer.price,
        name: this.columnNames.price,
        hasSearch: true,
        isSticky: true,
        type: 'number',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}receiverCount`,
        name: this.columnNames.receiverCount,
        hasSearch: true,
        isSticky: true,
        type: 'number',
        wrap: true,
      },
      {
        title: this.localizer.resellerName,
        name: this.columnNames.resellerName,
        hasSearch: true,
        noSorting: true,
        wrap: true,
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: this.localizer.senderNumber,
        name: this.columnNames.senderNumber,
        hasSearch: true,
        wrap: true,
        dir: 'ltr',
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: `${this.tableVocabs}campaign`,
        name: this.columnNames.title,
        hasSearch: true,
        wrap: true,
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: this.localizer.sendType,
        name: this.columnNames.sendType,
        hasSearch: true,
        type: 'enum',
        enumName: 'SendTypes',
        wrap: true,
      },
      {
        title: 'common.send',
        name: this.columnNames.sendSubType,
        type: 'enum',
        enumName: 'SendSubTypes',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.organization,
        name: this.columnNames.organizationName,
        hasSearch: true,
        wrap: true,
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: this.localizer.user,
        name: this.columnNames.userName,
        hasSearch: true,
        wrap: true,
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: `${this.tableVocabs}smsParts`,
        name: this.columnNames.smsParts,
        hasSearch: true,
        type: 'number',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}status`,
        name: this.columnNames.status,
        hasSearch: true,
        type: 'enum',
        enumName: 'OutBoxStatus',
        wrap: true,
      },
      {
        title: 'common.creationLocation',
        name: this.columnNames.creationLocation,
        hasSearch: true,
        type: 'enum',
        enumName: 'EntityCreationLocation',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}isResend`,
        name: this.columnNames.isResend,
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: 'common.cancel',
        name: this.columnNames.isShouldNotResend,
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: 'common.isPostPaid',
        name: this.columnNames.isPostPaid,
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: 'common.voiceMessage',
        name: this.columnNames.voiceMessageId,
        type: 'boolean',
        hasSearch: false,
        noSorting: true,
      },
      {
        title: 'common.sentDateTime',
        name: this.columnNames.sentDateTime,
        hasSearch: true,
        type: 'dateTime',
        dateDefaultValue: DateTimeService.getTodayDate().toUTC(),
        isRequired: true,
        wrap: true,
        isSticky: true,
        order: 99,
      },
    ];
    try {
      if (!this.showReseller)
        this.tableColumns = this.tableColumns.filter(
          (column) => column.name !== this.columnNames.resellerName
        );
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  setDefaultTableSort() {
    const isExistsSortObject = this.sortObject.some((item) =>
      item.columnName.includes(this.columnNames.sentDateTime)
    );
    if (!isExistsSortObject)
      this.sortObject.push({
        columnName: this.columnNames.sentDateTime,
        sort: 1,
      });
  }
  defaultFilters = [
    {
      field: this.columnNames.sentDateTime,
      operator: 3,
      value: DateTimeService.getTodayDate().toUTC(),
    },
    {
      field: this.columnNames.sentDateTime,
      operator: 4,
      value: DateTimeService.getSimpleTodayDate().toUTC(),
    },
  ];

  setDefaultDateSearch() {
    try {
      let greaterThan = {
        field: this.columnNames.sentDateTime,
        operator: 3,
        value: DateTimeService.getTodayDate().toUTC(),
      };

      let lessThan = {
        field: this.columnNames.sentDateTime,
        operator: 4,
        value: DateTimeService.getSimpleTodayDate().toUTC(),
      };
      this.comparisonObjects.push({
        comparisonObjects: [greaterThan, lessThan],
        logicalComparisonOperator: LogicalComparisonOperator.And,
      });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  onClearQueryString() {
    this.tabsDynamicComponent.refreshTab(this.tabName);
  }

  //#endregion

  override bindTableData() {
    if (this.isBindTableData) return;
    this.isBindTableData = true;
    this.tableDataSubject$
      .pipe(
        switchMap((res) => {
          this.pageConfig.loading = true;
          return this.getPaginationApi().pipe(
            map((res) => res.data),
            tap((result) => {
              this.modelWithPagination$ = of(result);
            }),
            finalize(() => (this.pageConfig.loading = false)),
            catchError((error) => {
              this.pageConfig.isEmpty = false;
              this.pageConfig.hasError = true;
              return error;
            }),
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.pageConfig.loading = false;
            })
          );
        })
      )
      .subscribe({
        next: (res) => {
          this.checkSelectedItems();
          this.getPaginationData(res);
          this.items = res.items;
          this.items$ = of(this.items);
          res.items?.length > 0
            ? (this.pageConfig.isEmpty = false)
            : (this.pageConfig.isEmpty = true);
          this.pageConfig.loading = false;
          this.totalReceivers = 0;
          this.totalPrice = 0;
          // if (this.count) {
          //   this.refreshCount();
          // }
        },
        error: (error) => {
          ConsoleService.error('error', error);
          this.isBindTableData = false;
        },
      });
  }

  setSelectedFields() {
    this.selectedFields = [];
    this.selectedFields = Object.assign([], this.fixedFields);
    const data = TableColumnStateService.getData(this.tagName);
    let allHaveIsHide: boolean = false;
    if (data)
      allHaveIsHide = Object.values(data).every(
        (item: any) => 'isHide' in item
      );
    if (data && allHaveIsHide) {
      Object.keys(data).forEach((key) => {
        if (!data[key]?.isHide) this.selectedFields.push(key);
      });
    } else
     this.selectedFields = Object.values(this.columnNames);

    if (!this.showReseller)
      this.selectedFields = this.selectedFields.filter(
        (s) => s !== this.columnNames.resellerName
      );
  }

  getPaginationBody() {
    //if advanced search exists
    let body;
    if (Object.keys(this.body).length != 0)
      body = this.getFilters(this.body.body).body;
    else body = this.getBaseModelWithPaginationParams();
    this.calTotalApiBody = body;
    return body;
  }
  //#region api

  //resend Get
  resendOutBoxMessageGetApi(scheduledMessageId: number) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesResendOutBoxMessageGet()
      .pipe(
        this.basePipeOperators(),
        tap((res) => {
          if (res) this.selectedIds = [];
        })
      )
      .subscribe((res) => {
        if (res)
          this.checkIsResendByAutomaticResendApi(scheduledMessageId).subscribe(
            (resendApiResult) => {
              this.showConfirmDialog(
                resendApiResult?.data?.message,
                resendApiResult?.data?.isResent
              ).subscribe((dialogResult) => {
                if (dialogResult) {
                  this.callGroupResendApi([scheduledMessageId]);
                }
              });
            }
          );
      });
  }

  //resend Post
  resendOutBoxMessageApi(ids: number[]) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesResendOutBoxMessagePost({
        body: { scheduledMessageIds: ids },
      })
      .pipe(
        this.basePipeOperators(),
        tap((res) => {
          if (res?.isSucceed) this.selectedIds = [];
        })
      )
      .subscribe(this.baseObserver);
  }

  //isShouldNotResend Post
  isShouldNotResendApi(scheduledMessageId: number, isShouldNotResend: boolean) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesUpdateIsShouldNotResendPut({
        body: {
          isShouldNotResend: !isShouldNotResend,
          scheduledMessageId: scheduledMessageId,
        },
      })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //StopSending Post
  isStopSendingApi(scheduledMessageId: number) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesStopSendingOutboxMessage({
        body: {
          scheduledMessageId: scheduledMessageId,
        },
      })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //resend dynamic p2p outBoxMessage Get
  resendDynamicOutBoxMessageGetApi(scheduledMessageId: number) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesResendDynamicOutBoxMessageGet()
      .pipe(
        this.basePipeOperators(),
        tap((res) => {
          if (res) this.selectedIds = [];
        })
      )
      .subscribe((res) => {
        if (res)
          this.checkIsResendByAutomaticResendApi(scheduledMessageId).subscribe(
            (resendApiResult) => {
              this.showConfirmDialog(
                resendApiResult?.data?.message,
                resendApiResult?.data?.isResent
              ).subscribe((dialogResult) => {
                if (dialogResult) {
                  this.callGroupResendApi([scheduledMessageId]);
                }
              });
            }
          );
      });
  }

  //resend dynamic p2p outBoxMessage Post
  resendDynamicOutBoxMessageApi(ids: number[]) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesResendDynamicOutBoxMessagePost({
        body: { scheduledMessageIds: ids },
      })
      .pipe(
        this.basePipeOperators(),
        tap((res) => {
          if (res?.isSucceed) this.selectedIds = [];
        })
      )
      .subscribe(this.baseObserver);
  }

  //get OutboxMessage By ScheduledMessageId
  getOutboxMessageByScheduledMessageIdApi(
    ScheduledMessageId: number,
    sendType?: SendTypes
  ) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesGetOutboxMessageByScheduledMessageId({
        scheduledMessageId: ScheduledMessageId,
      })
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        try {
          this.detailModel = res.data;
          if ([SendTypes.Bulk, SendTypes.Bulk2].includes(sendType))
            this.isShowSendBulkDetail = true;
          else if ([SendTypes.Smart].includes(sendType)) {
            this.isShowSendSmartDetail = true;

            this.isShowSmartSmsRecipientInDetail = true;
          } else if (
            [SendTypes.Phonebook, SendTypes.Dynamic, SendTypes.Club].includes(
              sendType
            )
          ) {
            this.isShowPhonebookRecipientInDetail = true;
            this.isShowDetail = true;
          } else this.isShowDetail = true;
        } catch (error) {
          this.loggerService.logError(
            this.tagName,
            error,
            EventTypes.smsReport
          );
        }
      });
  }

  // resend By Delivery Status Get
  resendByDeliveryStatusGetApi() {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesResendByDeliveryStatusGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) this.isShowResendByDeliveryStatus = true;
      });
  }

  // resend Dynamic By DeliveryStatus Get
  resendDynamicByDeliveryStatusGetApi() {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesResendDynamicByDeliveryStatusGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) this.isShowResendByDeliveryStatus = true;
      });
  }

  //Delete
  baseDeleteApi(id: number) {
    this.unSubs();
    this.pageConfig.loading = true;
    this.lastSelectedItemId = id;

    this.currentService
      .outBoxMessagesDeleteOutBoxMessage({ body: { scheduledMessageId: id } })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseDeleteObserver);
  }

  //Group Deletion
  deleteOutBoxMessagesByListApi(ids: number[]) {
    this.unSubs();
    this.pageConfig.loading = true;
    this.lastSelectedItemId = ids;

    this.currentService
      .outBoxMessagesDeleteOutBoxMessagesByList({
        body: { scheduledMessageIds: ids },
      })
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.selectedIds = [])),
        tap((res) => {
          if (res?.isSucceed) this.selectedIds = [];
        })
      )
      .subscribe(this.baseGroupDeleteObserver);
  }

  //NewDeliveryStatus
  applyingToGetNewDeliveryStatusGetApi(scheduledMessageId) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.outboxMessageNumbersService
      .outboxMessageNumbersApplyingToGetNewDeliveryStatusGet({})
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        try {
          if (res) {
            let data = {
              scheduledMessageId: scheduledMessageId,
              DeliveryStatusEnumValues: this.DeliveryStatusEnumValues,
              type: 'getNewDeliveryStatus',
            };

            const modalResult = this.dialogService.openModal({
              component: EditSendReceiversDeliveryStatus,
              data: data,
            });

            modalResult
              .afterClosed()
              .pipe(
                takeUntil(this.onDestroy$),
                tap(() => {
                  AppDomService.removeModalScrollClassFromHtml();
                })
              )
              .subscribe((res) => {
                if (res) {
                  this.getTableData();
                  this.cd.detectChanges();
                }
              });
          }
        } catch (error) {
          this.loggerService.logError(
            this.tagName,
            error,
            EventTypes.smsReport
          );
        }
      });
  }

  //updateDeliveryStatusByDeliveryStatus
  updateDeliveryStatusByDeliveryStatusAndScheduledMessageIdGetApi(
    scheduledMessageId
  ) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.outboxMessageNumbersService
      .outboxMessageNumbersUpdateDeliveryStatusByDeliveryStatusAndScheduledMessageIdGet(
        {}
      )
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        try {
          if (res) {
            let data = {
              scheduledMessageId: scheduledMessageId,
              DeliveryStatusEnumValues: this.DeliveryStatusEnumValues,
              type: 'all',
            };

            const modalResult = this.dialogService.openModal({
              component: EditSendReceiversDeliveryStatus,
              data: data,
            });

            modalResult
              .afterClosed()
              .pipe(
                takeUntil(this.onDestroy$),
                tap(() => {
                  AppDomService.removeModalScrollClassFromHtml();
                })
              )
              .subscribe((res) => {
                if (res) {
                  this.getTableData();
                  this.cd.detectChanges();
                }
              });
          }
        } catch (error) {
          this.loggerService.logError(
            this.tagName,
            error,
            EventTypes.smsReport
          );
        }
      });
  }

  //ResendHistory
  getResendHistoryByScheduledIdApi(scheduledMessageId: number) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.outboxMessageResendHistoriesService
      .outBoxMessageResendHistoriesGetOutBoxMessageResendHistoryByScheduledId({
        body: {
          pageNumber: 1,
          pageSize: 30,
          scheduledMessageId: scheduledMessageId,
        },
      })
      .pipe(this.basePipeOperators())
      .subscribe((model) => {
        try {
          if (model) {
            try {
              this.resendDetailWithPagination$ = of(model);
              this.isShowResendDetail = true;
            } catch (error) {
              this.loggerService.logError(
                this.tagName,
                error,
                EventTypes.smsReport
              );
            }
          }
        } catch (error) {
          this.loggerService.logError(
            this.tagName,
            error,
            EventTypes.smsReport
          );
        }
      });
  }

  updateStatusGetApi(scheduledMessageId: number, status: OutBoxStatus) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesUpdateStatusGet({ id: scheduledMessageId.toString() })
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          let data = {
            scheduledMessageId: scheduledMessageId,
            status: status,
            OutBoxStatusEnumValues: this.OutBoxStatusEnumValues,
          };

          const modalResult = this.dialogService.openModal({
            component: OutboxMessagesEditStatusComponent,
            data: data,
          });

          modalResult
            .afterClosed()
            .pipe(
              takeUntil(this.onDestroy$),
              tap(() => {
                AppDomService.removeModalScrollClassFromHtml();
              })
            )
            .subscribe((res) => {
              if (res) {
                this.getTableData();
                this.cd.detectChanges();
              }
            });
        }
      });
  }

  InboxExcelWithMoreDetailReportApi() {
    this.excelFileService
      .excelCreateOutboxMessagesExcelWithSeparatedDeliveryColumnsGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          let data = {
            title: `${this.pageVocabs}outboxExcelWithMoreDetail`,
            comparisonObjects: this.comparisonObjects,
            sortObject: this.sortObject,
            body: this.body.body,
            excelType: this.excelType,
            isMoreDetailType: true,
            organizationId: this.organizationId,
          };

          const modalResult = this.dialogService.openModal({
            component: CreateOutboxMessagesExcelDialogComponent,
            data: data,
          });

          modalResult
            .afterClosed()
            .pipe(
              takeUntil(this.onDestroy$),
              tap(() => {
                AppDomService.removeModalScrollClassFromHtml();
              })
            )
            .subscribe();
        }
      });
  }

  InboxExcelReportApi() {
    this.excelFileService
      .excelCreateOutboxMessagesExcelGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          let data = {
            title: `${this.pageVocabs}outboxExcel`,
            comparisonObjects: this.comparisonObjects,
            sortObject: this.sortObject,
            body: this.body.body,
            excelType: this.excelType,
            organizationId: this.organizationId,
          };

          const modalResult = this.dialogService.openModal({
            component: CreateOutboxMessagesExcelDialogComponent,
            data: data,
          });

          modalResult
            .afterClosed()
            .pipe(
              takeUntil(this.onDestroy$),
              tap(() => {
                AppDomService.removeModalScrollClassFromHtml();
              })
            )
            .subscribe();
        }
      });
  }

  getShortcutDetailModelApi(smsText: string, voiceMessageId: number) {
    try {
      let updateGetApiMethodName: string = '';
      let paramIdName: string = 'id';
      if (voiceMessageId) {
        updateGetApiMethodName = 'voiceMessagesUpdateUploadedFileGet';
      }

      this.unSubs();
      this.pageConfig.loading = true;
      this.voiceMessageService[updateGetApiMethodName]({
        [paramIdName]: voiceMessageId,
      })
        .pipe(this.basePipeOperators())
        .subscribe((res) => {
          if (res?.isSucceed) {
            if (voiceMessageId) {
              this.openShortcutDetail(smsText, res?.data);
              return;
            }
          }
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  //Refresh count
  calcTotalApi() {
    this.unSubs();

    this.calcTotalApiLoading = true;
    this.currentService[this.getTotalCountApiMethodName]({
      body: {
        ...this.calTotalApiBody,
        organizationId: this.organizationId,
      },
    })
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.calcTotalApiLoading = false))
      )
      .subscribe((result) => {
        this.totalReceivers = result?.data.totalReceivers;
        this.totalPrice = result?.data.totalPrice;
        this.count = result?.data.count;
        this.getPaginationData(result?.data.count);
      });
  }

  override getPaginationData(count) {
    try {
      if (!count) return;

      let totalPages = Math.ceil(count / this.paginationModel?.pageSize);
      let data = {
        totalCount: count,
        totalPages: totalPages,
        pageIndex: this.paginationModel.pageIndex,
        pageNumber: this.paginationModel.pageNumber,
        hasPreviousPage: this.paginationModel.pageNumber > 1 ? true : false,
        hasNextPage:
          this.paginationModel.pageNumber < totalPages ? true : false,
      };
      this.paginationModel = this.dynamicTableService.getPaginationData(
        data,
        this.paginationModel
      );
      this.pageConfig.tableConfig.paginationModel = this.paginationModel;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  showConfirmDialog(message: string, isResent: boolean) {
    const dialogResult = this.getConfirmDialog({
      title: 'messages.globalConfirmationMessage',
      isConfirmDelete: false,
      wordBreak: false,
      isWarning: true,
      confirmBtnColor: 'btn-warning',
      content: isResent ? message : '',
    });
    return dialogResult.afterClosed().pipe(takeUntil(this.onDestroy$));
  }

  //#endregion

  onGroupResend() {
    if (this.selectedIds?.length === 0) return;
    this.checkOutboxMessagesIsResendByAutomaticResend(
      this.selectedIds
    ).subscribe((resultApi) => {
      this.showConfirmDialog(
        resultApi.data?.message,
        resultApi.data?.isContainAnyResent
      ).subscribe((dialogResult) => {
        if (dialogResult) {
          this.callGroupResendApi(this.selectedIds);
        }
      });
    });
  }

  //#region search & filter
  onAdvanceSearch() {
    try {
      const modalResult = this.dialogService.openModal({
        component: OutboxAdvancedSearchFormComponent,
        data: { filterData: this.body },
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((filter) => {
          if (filter) {
            this.isAdvancedSearch = true;
            if (filter.filter.startDate) {
              this.comparisonObjects = this.removePreviousSendDateTime();
              let sendDateTimeField = this.getSendDateTimeField();
              if (sendDateTimeField) sendDateTimeField.dateDefaultValue = null;
            }
            this.paginationModel.pageNumber = 1;

            this.getFilters(filter.filter);
            this.getTableData();
          }
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  getFilters(filter: GetUserWithPaginationQuery) {
    try {
      if (filter.startDate || filter.endDate) {
        this.comparisonObjects = this.removePreviousSendDateTime();
      } else {
        filter.startDate = undefined;
        filter.endDate = undefined;

        //set default value if advanced date cleared
        let sendDateTimeField = this.getSendDateTimeField();
        if (sendDateTimeField) {
          sendDateTimeField.dateDefaultValue =
            DateTimeService.getTodayDate().toUTC();
          let greaterThan = {
            field: this.columnNames.sentDateTime,
            operator: ComparisonOperator.GreaterThan,
            value: DateTimeService.getTodayDate().toUTC(),
          };

          let lessThan = {
            field: this.columnNames.sentDateTime,
            operator: ComparisonOperator.LessThan,
            value: DateTimeService.getSimpleTodayDate().toUTC(),
          };

          if (!this.checkSentDateTimeFieldExists())
            this.comparisonObjects.push({
              comparisonObjects: [greaterThan, lessThan],
              logicalComparisonOperator: LogicalComparisonOperator.And,
            });
        }
      }

      this.body = {
        body: {
          ...this.getBaseModelWithPaginationParams(),
          toNumber: filter.toNumber,
          phonebookIds: filter.phonebookIds,
          zipCode: filter.zipCode,
          guild: filter.guild,
          zone: filter.zone,
          description: filter.description,
          smsText: filter.smsText,
          endDate: filter.endDate,
          startDate: filter.startDate,
          deliveryStatus: filter.deliveryStatus,
          sendStatus: filter.sendStatus,
        },
      };

      return this.body;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  checkSentDateTimeFieldExists(): boolean {
    return this.comparisonObjects.some((item) =>
      item.comparisonObjects?.some(
        (obj) => obj.field === this.columnNames.sentDateTime
      )
    );
  }
  //#endregion

  //#region resend

  onSingleResend(scheduledMessageId: number, sendType: SendTypes) {
    try {
      if (!scheduledMessageId) return;
      this.selectedScheduledMessageSendType = sendType;
      this.scheduledMessageId = scheduledMessageId;

      switch (sendType) {
        case SendTypes.P2P:
        case SendTypes.Dynamic:
          this.resendDynamicOutBoxMessageGetApi(scheduledMessageId);
          break;
        default:
          this.resendOutBoxMessageGetApi(scheduledMessageId);
          break;
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  callGroupResendApi(ids?: number[]) {
    try {
      switch (this.selectedScheduledMessageSendType) {
        case SendTypes.P2P:
        case SendTypes.Dynamic:
          this.resendDynamicOutBoxMessageApi(ids);
          break;

        default:
          this.resendOutBoxMessageApi(ids);
          break;
      }

      this.cd.detectChanges();
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  //#endregion

  //#region on show

  onShowReceivers(id: number, sendDateTime: string, sendType?: SendTypes) {
    this.showParent = false;
    this.setParentQueryString();
    try {
      this.scheduledMessageId = id;
      this.defaultSearchValueService.defaultDateValue = sendDateTime;

      if (sendType == SendTypes.Smart || sendType == SendTypes.SmartP2P)
        IsSmartTypeService.isSmart = true;
      else IsSmartTypeService.isSmart = false;

      if (
        [
          SendTypes.Dynamic,
          SendTypes.P2P,
          SendTypes.ApiP2P,
          SendTypes.SmartP2P,
          SendTypes.OccasionPhonebook,
        ].includes(sendType)
      ) {
        this.scheduledMessageIdsStorageService.scheduledMessageIds.push(id);
        this.dynamicTabService.setNewTab('');
        this.dynamicTabService.setNewTab(
          'dynamicSendReceivers?' +
            '?' +
            QueryStringTypesEnum.ScheduledMessage_key +
            '=' +
            this.scheduledMessageId
        );
        this.dynamicTabService.setActiveTab(
          'dynamicSendReceivers?' +
            '?' +
            QueryStringTypesEnum.ScheduledMessage_key +
            '=' +
            this.scheduledMessageId
        );
      } else {
        this.scheduledMessageIdsStorageService.scheduledMessageIds.push(id);
        this.dynamicTabService.setNewTab('');
        this.dynamicTabService.setNewTab(
          'sendReceivers?' +
            '?' +
            QueryStringTypesEnum.ScheduledMessage_key +
            '=' +
            this.scheduledMessageId
        );
        this.dynamicTabService.setActiveTab(
          'sendReceivers?' +
            '?' +
            QueryStringTypesEnum.ScheduledMessage_key +
            '=' +
            this.scheduledMessageId
        );
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  onShowPhonebookRecipient(id: number) {
    this.showParent = false;
    this.setParentQueryString();
    this.isShowPhonebookRecipient = true;
    this.scheduledMessageId = id;
  }

  onShowUsersOutboxMessages() {
    this.showParent = false;
    this.setParentQueryString();
  }

  onShowResendByDeliveryStatus(
    model: GetOutboxMessagesWithPaginationDto,
    setUrl = true
  ) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.ResendByDeliveryStatus_key +
          '=' +
          model.scheduledMessageId +
          '_' +
          model.userName +
          '_' +
          model.organizationId +
          '_' +
          model.organizationName +
          '_' +
          model.createdBy +
          '_' +
          model.sendType +
          '_' +
          model.smsText +
          '_' +
          model.voiceMessageGroupId
      );
    }
    try {
      this.selectedScheduledMessageSendType = model.sendType;
      this.resendByDeliveryStatusModel = {
        scheduledMessageId: model.scheduledMessageId,
        smsBody: model.smsText,
        senderNumberId: model.privateNumberId,
      };

      this.selectedUserId = model.createdBy;
      this.selectedOrganizationId = model.organizationId;
      this.selectedUserName = model.userName;
      this.selectedOrganizationName = model.organizationName;
      this.cpType = model.cpType;
      this.voiceMessageId = model.voiceMessageId;
      this.voiceMessageGroupId = model.voiceMessageGroupId;

      switch (this.selectedScheduledMessageSendType) {
        case SendTypes.P2P:
        case SendTypes.Dynamic:
          this.resendDynamicByDeliveryStatusGetApi();
          break;
        default:
          this.resendByDeliveryStatusGetApi();
          break;
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  checkIsResendByAutomaticResendApi(
    scheduledMessageId
  ): Observable<CustomResultOfCheckIsResendByAutomaticResendDto> {
    return this.currentService
      .outBoxMessagesCheckIsResendByAutomaticResend({
        body: { scheduledMessageId: scheduledMessageId },
      })
      .pipe(this.basePipeOperators());
  }

  checkOutboxMessagesIsResendByAutomaticResend(
    scheduledMessageIds: number[]
  ): Observable<CustomResultOfCheckOutboxMessagesIsResendByAutomaticResendDto> {
    return this.currentService
      .outBoxMessagesCheckOutboxMessagesIsResendByAutomaticResend({
        body: { scheduledMessageIds: scheduledMessageIds },
      })
      .pipe(this.basePipeOperators());
  }

  onShowResendDetail(scheduledMessageId: number, setUrl = true) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.ResendDetails_key +
          '=' +
          scheduledMessageId
      );
    }
    this.scheduledMessageId = scheduledMessageId;
    this.getResendHistoryByScheduledIdApi(scheduledMessageId);
  }

  onShowTransactions(scheduledMessageId) {
    this.scheduledMessageIdsStorageService.scheduledMessageId =
      scheduledMessageId;
    this.dynamicTabService.setNewTab('');
    this.dynamicTabService.setNewTab(
      'outboxTransactions?' +
        QueryStringTypesEnum.ScheduledMessage_key +
        '=' +
        scheduledMessageId
    );
    this.dynamicTabService.setActiveTab(
      'outboxTransactions?' +
        QueryStringTypesEnum.ScheduledMessage_key +
        '=' +
        scheduledMessageId
    );
  }

  //#endregion

  //#region event method

  onDetail(scheduledMessageId: number, sendType?: SendTypes, setUrl = true) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Details_key +
          '=' +
          scheduledMessageId +
          '_' +
          sendType
      );
    }
    this.scheduledMessageId = scheduledMessageId;
    this.isShowPhonebookRecipientInDetail = false;
    this.isShowSmartSmsRecipientInDetail = false;
    this.getOutboxMessageByScheduledMessageIdApi(scheduledMessageId, sendType);
  }

  onEditStatus(scheduledMessageId: number, status: OutBoxStatus) {
    try {
      this.unSubs();
      this.updateStatusGetApi(scheduledMessageId, status);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  onIsShouldNotResend(scheduledMessageId, isShouldNotResend) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .outBoxMessagesUpdateIsShouldNotResendGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res)
          this.isShouldNotResendConfirmation(
            scheduledMessageId,
            isShouldNotResend
          );
      });
  }

  isShouldNotResendConfirmation(
    scheduledMessageId: number,
    isShouldNotResend: boolean
  ) {
    const dialogResult = this.getConfirmDialog({
      title: 'messages.globalConfirmationMessage',
      isConfirmDelete: false,
      wordBreak: false,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result)
          this.isShouldNotResendApi(scheduledMessageId, isShouldNotResend);
      });
  }

  onStopSending(scheduledMessageId) {
    this.unSubs();
    this.isStopSending(scheduledMessageId);
  }

  isStopSending(scheduledMessageId: number) {
    const dialogResult = this.getConfirmDialog({
      title: 'messages.globalConfirmationMessage',
      content: 'messages.confirmByWritingTextMessageForStopSending',
      isConfirmDelete: false,
      wordBreak: false,
      confirmByWritingText: true,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) this.isStopSendingApi(scheduledMessageId);
      });
  }

  onEditReceiversDeliveryStatus(scheduledMessageId: number) {
    this.updateDeliveryStatusByDeliveryStatusAndScheduledMessageIdGetApi(
      scheduledMessageId
    );
  }

  onDelete(id: any) {
    let dialogResult = this.dialogService.confirmDialog({
      isConfirmDelete: true,
      content: id,
    });
    dialogResult.afterClosed().subscribe((result) => {
      if (result) {
        this.baseDeleteApi(id);
      }
    });
  }

  onChangeOrganization(organizationId: number) {
    this.organizationId = organizationId;
    this.getTableData();
  }

  onGetNewDeliveryStatus(scheduledMessageId: number) {
    this.applyingToGetNewDeliveryStatusGetApi(scheduledMessageId);
  }

  //#endregion

  calcTotalSumCurrentPage(items: GetOutboxMessagesWithPaginationDto[]) {
    try {
      this.totalReceiversCurrentPage = items
        .map((item) => item.receiverCount)
        .reduce((acc, curr) => {
          return acc + curr;
        }, 0);

      this.totalPriceCurrentPage = items
        .map((item) => item.price)
        .reduce((acc, curr) => {
          return acc + curr;
        }, 0);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  callGroupDeletion() {
    this.deleteOutBoxMessagesByListApi(this.selectedIds);
    this.cd.detectChanges();
  }

  goSendSmsPage(tabName: string) {
    try {
      this.dynamicTabService.setNewTab('');
      this.dynamicTabService.setNewTab(tabName);
      this.dynamicTabService.setActiveTab(tabName);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  getResendByDeliveryStatusResult(result) {
    this.showParent = true;
    this.getParentQueryString();
    this.isShowResendByDeliveryStatus = false;
    this.pageConfig.loading = true;

    let timeOut = setTimeout(() => {
      this.pageConfig.loading = false;
      clearTimeout(timeOut);
    }, 500);

    if (result) {
      this.selectedIds = [];
      this.getTableData();
    }
  }

  closeResendDetail() {
    this.pageConfig.loading = true;
    let timeOut = setTimeout(() => {
      this.pageConfig.loading = false;
      clearTimeout(timeOut);
    }, 500);
    this.isShowResendDetail = false;
    this.showParent = true;
    this.getParentQueryString();
  }

  async getAllCheckBoxesAsync(): Promise<NodeListOf<Element>> {
    return AppDomService.getElementsByQueryAsync(
      `${this.tabName} [data-checkbox-id]`
    );
  }

  // DeliveryStatusChart
  deliveryStatusChart(scheduledMessageId) {
    // this.unSubs();
    if (this.subscription) this.subscription.unsubscribe();

    this.pageConfig.loading = true;
    this.subscription = this.outboxMessageNumbersService
      .outboxMessageNumbersGetOutboxMessageDeliveryStatusStatisticsByScheduledMessageId(
        {
          body: { scheduledMessageId: scheduledMessageId },
        }
      )
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        try {
          if (res) {
            let data = {
              scheduledMessageId: scheduledMessageId,
              DeliveryStatusEnumValues: this.DeliveryStatusEnumValues,
              deliveryStatusData: res?.data,
            };

            const modalResult = this.dialogService.openModal({
              component: DeliveryStatusStats,
              data: data,
              width: '48vw',
            });

            const sub = modalResult
              .afterClosed()
              .pipe(
                takeUntil(this.onDestroy$),
                tap(() => {
                  AppDomService.removeModalScrollClassFromHtml();
                })
              )
              .subscribe((res) => {
                sub.unsubscribe();
              });
          }
        } catch (error) {
          this.loggerService.logError(
            this.tagName,
            error,
            EventTypes.smsReport
          );
        }
      });
  }

  onExcelReport() {
    this.InboxExcelReportApi();
  }

  onExcelWithMoreDetailReport() {
    this.InboxExcelWithMoreDetailReportApi();
  }

  removePreviousSendDateTime() {
    return this.comparisonObjects.filter(
      (obj) =>
        !obj.comparisonObjects.some(
          (compObj) => compObj.field === this.columnNames.sentDateTime
        )
    );
  }

  getSendDateTimeField() {
    return this.tableColumns.find(
      (item) => item.name == this.columnNames.sentDateTime
    );
  }

  //#region shortcut detail
  openShortcutDetail(smsText: string, model) {
    try {
      this.unSubs();

      let data = {
        model: {
          smsText: smsText,
          content: model,
        },
      };
      const modalResult = this.dialogService.openModal({
        component: VoiceSmsDetailModalComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }
  //#endregion
}
